import React, { useEffect } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment-timezone";

import { useApi } from "../../../hooks/useApi";
import { useFormData } from "../../../hooks/useFormData";
import FileUploadButton from "../../common/FileUploadButton";
import { ucwords } from "../../../lib/stringHelpers";

const DEFAULT_DATE = moment().add(3, "days");

const DEFAULT_VALS = {
  startDate: DEFAULT_DATE.format("YYYY-MM-DD"),
  startTime: "",
  endTime: "",
  estimatedTime: 2,
  notes: "",
  fileUrl: null,
  paymentMethod: null,
  helperServiceFee: 20,
  customerServiceFee: 20,
  helperRate: "",
  _methods: [],
};

const getHelperRate = (booking) => {
  const helperId = booking?.jobRequest?.helper;
  const match = (booking?.jobRequest?.requestedHelperRates || []).find(
    (rate) => String(rate.helperId) === String(helperId)
  );

  if (match && match.helperRate != null) {
    return match.helperRate;
  }

  return booking?.helper?.helper?.hourlyRates?.standard ?? "";
};

const BookingEditForm = ({ booking, onSuccess, onCancel }) => {
  const { formData, onChange, setData } = useFormData({ ...DEFAULT_VALS });

  const { call: callApi, isLoading } = useApi({
    onError: (err) => toast.error(err.message),
  });

  const getUser = (userId) => {
    callApi(`/admin/users/${userId}`, "get", undefined, undefined, (res) => {
      setData({ _methods: res._paymentMethods }, true);
    });
  };

  useEffect(() => {
    if (booking) {
      const start = moment(booking.startDate);
      const end = moment(booking.endDate);
      setData({
        startDate: start.format("YYYY-MM-DD"),
        startTime: start.format("HH:mm"),
        endTime: end.format("HH:mm"),
        estimatedTime: booking.estimatedTime,
        reauth: false,
        paymentMethod: booking.jobRequest.paymentMethod,
        helperServiceFee: booking.jobRequest.serviceFee.helper * 100,
        customerServiceFee: booking.jobRequest.serviceFee.customer * 100,
        helperRate: getHelperRate(booking),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking]);

  useEffect(() => {
    if (booking.customer._id) {
      getUser(booking.customer._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.customer]);

  const onSubmit = (e) => {
    e.preventDefault();

    const startDateValue =
      formData.startDate || moment(booking.startDate).format("YYYY-MM-DD");
    const startTimeValue =
      formData.startTime || moment(booking.startDate).format("HH:mm");
    const startDt = moment(
      `${startDateValue} ${startTimeValue}`,
      "YYYY-MM-DD HH:mm"
    );

    const payload = {
      startDate: startDt.toDate(),
      endDate: moment(startDt).add(formData.estimatedTime, "hours").toDate(),
      estimatedTime: Number(formData.estimatedTime),
      notes: formData.notes,
      fileUrl: formData.fileUrl,
      paymentMethod: formData.paymentMethod,
      serviceFee: {
        helper: Number(formData.helperServiceFee) * 0.01,
        customer: Number(formData.customerServiceFee) * 0.01,
      },
      helperRate: Number(formData.helperRate),
    };

    callApi(
      `/admin/job-requests/${booking.jobRequest._id}/events/${booking._id}`,
      "put",
      undefined,
      payload,
      () => {
        onSuccess();
      }
    );
  };

  const onTimeChange = (e) => {
    const isStart = e.target.name === "startTime";
    const startDateValue =
      formData.startDate || moment(booking.startDate).format("YYYY-MM-DD");
    const parseDateTime = (time) =>
      moment(`${startDateValue} ${time || "00:00"}`, "YYYY-MM-DD HH:mm");
    let startDt = parseDateTime(formData.startTime || e.target.value);
    let endDt = parseDateTime(formData.endTime || e.target.value);
    let updates = {};

    if (isStart) {
      // we want to move the end time forward to maintain the hour difference
      startDt = parseDateTime(e.target.value);
      endDt = moment(startDt).add(formData.estimatedTime, "hours");

      updates = {
        startTime: startDt.format("HH:mm"),
        endTime: endDt.format("HH:mm"),
      };
    } else {
      // we want to calculate the new hour difference between start and end
      startDt = parseDateTime(formData.startTime);
      endDt = parseDateTime(e.target.value);
      if (endDt.isSameOrBefore(startDt)) {
        endDt.add(1, "day");
      }

      // mins need to be converted to hours in 0.5 increments
      const mins = endDt.diff(startDt, "minutes");

      updates = {
        startTime: startDt.format("HH:mm"),
        endTime: endDt.format("HH:mm"),
        estimatedTime: mins / 60,
      };
    }

    setData({ ...updates }, true);
  };

  const onHourChange = (e) => {
    let startDate =
      formData.startDate || moment(booking.startDate).format("YYYY-MM-DD");
    let startTime =
      formData.startTime || moment(booking.startDate).format("HH:mm");
    let endDt = moment(
      `${startDate} ${startTime}`,
      "YYYY-MM-DD HH:mm"
    ).add(e.target.value, "hours");
    setData({ endTime: endDt.format("HH:mm"), estimatedTime: e.target.value });
  };

  return (
    <form onSubmit={onSubmit}>
      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Helper Service Fee %</Form.Label>
            <Form.Control
              type="number"
              step={2.5}
              min={0}
              max={35}
              name="helperServiceFee"
              value={formData.helperServiceFee}
              onChange={onChange}
              required={true}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label>Customer Service Fee %</Form.Label>
            <Form.Control
              type="number"
              step={2.5}
              min={0}
              max={35}
              name="customerServiceFee"
              value={formData.customerServiceFee}
              onChange={onChange}
              required={true}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={6}>
          <Form.Group className="mb-3 required">
            <Form.Label>Hourly Rate</Form.Label>
            <Form.Control
              type="number"
              name="helperRate"
              min={0.01}
              step={0.01}
              value={formData.helperRate}
              onChange={onChange}
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={6}>
          <Form.Group className="mb-3 required">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={onChange}
              format="MM/DD/YYYY"
              required
            />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6}>
          &nbsp;
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={6}>
          <Form.Group className="mb-3 required">
            <Form.Label>Start Time</Form.Label>
            <Form.Control
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={onTimeChange}
              required
            />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Group className="mb-3 required">
            <Form.Label>End Time</Form.Label>
            <Form.Control
              type="time"
              name="endTime"
              value={formData.endTime}
              onChange={onTimeChange}
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={6}>
          <Form.Group className="mb-3 required">
            <Form.Label>Hours</Form.Label>
            <Form.Control
              type="number"
              name="estimatedTime"
              min={1}
              max={23}
              step={0.25}
              value={formData.estimatedTime}
              onChange={onHourChange}
              required
            />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Group className="mb-3">
            <Form.Label>Payment Methods</Form.Label>
            <Form.Select
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={onChange}
              required={true}
            >
              {isLoading ? (
                <option value="">Loading...</option>
              ) : (
                <option value="">Select a payment method</option>
              )}
              {formData._methods.length > 0
                ? formData._methods.map((m, idx) => {
                    return (
                      <option key={idx} value={m.id}>{`${ucwords(
                        m.card.display_brand
                      )} ${m.card.last4}`}</option>
                    );
                  })
                : null}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              onChange={onChange}
              value={formData.notes}
              placeholder="Optional notes..."
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Additional File</Form.Label>
            {formData.fileUrl ? (
              <p>
                <a href={formData.fileUrl} target="_blank" rel="noreferrer">
                  View Uploaded File
                </a>
              </p>
            ) : null}
            <div>
              <FileUploadButton
                onUpload={(url) =>
                  onChange({ target: { name: "fileUrl", value: url } })
                }
              />
            </div>
          </Form.Group>
        </Col>
      </Row>

      <div className="mt-4">
        <Button type="submit" variant="primary" disabled={isLoading}>
          Save Changes
        </Button>
        <Button variant="link" onClick={() => onCancel()} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default BookingEditForm;
